import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

interface Member {
  id: number;
  user_id: number;
  name: string;
  email: string;
  role_id: number;
  role_name: string;
  organization_id: number;
  organization_name: string;
  project_id: number;
  project_title: string;
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  last_active: string;
  joined_at: string;
  skills?: string[];
}

interface MembersResponse {
  success: boolean;
  data: Member[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface MemberStatsResponse {
  success: boolean;
  data: {
    totalMembers: number;
    activeMembers: number;
    inactiveMembers: number;
    teamLeaders: number;
    teamMembers: number;
    agents: number;
    guests: number;
    newToday: number;
  };
}

interface MemberFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  organization_id?: number;
  project_id?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

interface Role {
  id: number;
  name: string;
  description: string;
}

const transformErrorResponse = (error: any) => {
  if (error?.data?.message) {
    return { error: error.data.message };
  }
  return { error: 'An error occurred' };
};

export const membersApi = createApi({
  reducerPath: 'membersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/members',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Member'],
  endpoints: (builder) => ({
    getMembers: builder.query<MembersResponse, MemberFilters>({
      query: (filters = {}) => ({
        url: '/',
        params: filters,
      }),
      transformErrorResponse,
      providesTags: ['Member'],
    }),
    getMemberStats: builder.query<MemberStatsResponse, void>({
      query: () => '/stats',
      transformErrorResponse,
      providesTags: ['Member'],
    }),
    getMemberById: builder.query<{ success: boolean; data: Member }, number>({
      query: (id) => `/${id}`,
      transformErrorResponse,
      providesTags: ['Member'],
    }),
    getMembersByProject: builder.query<{ success: boolean; data: Member[] }, number>({
      query: (projectId) => `/project/${projectId}`,
      transformErrorResponse,
      providesTags: ['Member'],
    }),
    getMembersByOrganization: builder.query<{ success: boolean; data: Member[] }, number>({
      query: (organizationId) => `/organization/${organizationId}`,
      transformErrorResponse,
      providesTags: ['Member'],
    }),
    getAvailableRoles: builder.query<{ success: boolean; data: Role[] }, void>({
      query: () => '/roles',
      transformErrorResponse,
      providesTags: ['Member'],
    }),
    updateMemberRole: builder.mutation<{ success: boolean; message: string }, { id: number; roleId: number }>({
      query: ({ id, roleId }) => ({
        url: `/${id}/role`,
        method: 'PUT',
        body: { roleId },
      }),
      transformErrorResponse,
      invalidatesTags: ['Member'],
    }),
    removeMemberFromProject: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
      invalidatesTags: ['Member'],
    }),
  }),
});

export const {
  useGetMembersQuery,
  useGetMemberStatsQuery,
  useGetMemberByIdQuery,
  useGetMembersByProjectQuery,
  useGetMembersByOrganizationQuery,
  useGetAvailableRolesQuery,
  useUpdateMemberRoleMutation,
  useRemoveMemberFromProjectMutation,
} = membersApi; 